<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">In this activity, you will look at some information and complete 4 tasks.</p>
    <p class="mb-5">
      To design an interior effectively, you’ll first need to gain some understanding of the basic
      principles of interior design. Watch the following video to get an idea of these and how they
      work together to create a space that works.
    </p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />

    <h4 class="mb-5">The 6 Elements of Home Decor</h4>

    <table class="table default-table">
      <tr class="header" v-for="(item, index) in elements_of_decor" :key="index">
        <th width="30%">{{ item.title }}:</th>
        <td>{{ item.description }}</td>
      </tr>
    </table>

    <h4 class="mb-5">Task 1: Principles of Home Decor: My Home</h4>
    <p class="mb-5">
      Look around the rooms in your home. Using the table below, rate each of the rooms out of 5 for
      each of the elements. When you’ve finished this, give each room a total score by adding each
      rating for that room.
    </p>

    <table class="table default-table">
      <tr class="header">
        <th width="20%">Element</th>
        <th width="20%">Your Bedroom</th>
        <th width="20%">Kitchen</th>
        <th width="20%">Living Room</th>
        <th width="20%">Bathroom</th>
      </tr>
      <tr class="header" v-for="(item, index) in elements_of_decor" :key="index">
        <th class="smallpadding text-left pl-10">{{ item.title }}</th>
        <td v-for="index2 in 4" :key="index2">
          <text-block
            v-model="data['question' + index + '_' + index2]"
            :editable="isEditable"
            iclass="'mb-0'"
          />
        </td>
      </tr>
      <tr class="header">
        <th class="smallpadding text-left pl-10"><u>Total</u></th>
        <td v-for="index2 in 4" :key="index2">
          <text-block
            v-model="data['question7' + '_' + index2]"
            :editable="isEditable"
            iclass="'mb-0'"
          />
        </td>
      </tr>
    </table>
    <p class="mb-5">
      Once you’ve done this, select the room with the lowest score. You’re now going to apply what
      you know about interior design to come up with ideas to improve this room.
    </p>
    <p class="mb-5">
      Using the following sites and your own ideas write down some ideas on how to improve the
      emphasis, contrast, rhythm, balance, scale & proportion, and harmony of your chosen room. You
      may like to copy pictures and save them in a document or cut out pictures from a magazine to
      help.
    </p>
    <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Useful Sites:</u></heading>
    <p class="mb-5">
      <a href="https://www.bedbathntable.com.au/home-decor" target="_blank"
        >https://www.bedbathntable.com.au/home-decor</a
      >
    </p>
    <p class="mb-5">
      <a href="https://www.templeandwebster.com.au/home-decor" target="_blank"
        >https://www.templeandwebster.com.au/home-decor</a
      >
    </p>
    <p class="mb-5">And you’ll find lots more sites and ideas here:</p>
    <p class="mb-5">
      <a href="https://www.finder.com.au/buy-home-decor-online-australia" target="_blank"
        >https://www.finder.com.au/buy-home-decor-online-australia</a
      >
    </p>
    <p class="mb-5">
      Pricing a job is an important aspect of being an interior designer. A big part of arriving at
      a price for a job is working out how much the materials required will be.
    </p>

    <h4 class="mb-5">Task 2: Ideas for Improvement</h4>
    <p class="mb-5">
      Use the table below to record your ideas to improve each element in the room and then record
      the cost of these improvements in the right-hand column. You can find approximate costs using
      online stores. If you can’t find the cost of exactly what you’re looking for, use the cost of
      the closest thing you can find.
    </p>
    <p class="mb-5">
      Finally, add these up to find the total costs of your proposed improvements and record this in
      the table too.
    </p>

    <table class="table default-table">
      <tr class="header">
        <th colspan="3">IDEAS FOR IMPROVEMENT</th>
      </tr>
      <tr class="header">
        <th width="25%">Room Name:</th>
        <td colspan="2">
          <text-block v-model="data['question8']" :editable="isEditable" iclass="'mb-0'" />
        </td>
      </tr>
      <tr class="header">
        <th width="25%"></th>
        <th width="50%">Ideas for Improvement:</th>
        <th width="25%">Cost:</th>
      </tr>
      <tr class="header" v-for="(item, index) in elements_of_decor" :key="index">
        <th class="smallpadding text-left pl-10">{{ item.title }}</th>
        <td>
          <textarea-block v-model="data['question' + (8 + index) + '_2']" :editable="isEditable" />
        </td>
        <td style="vertical-align: top">
          <text-block
            v-model="data['question' + (8 + index) + '_1']"
            :editable="isEditable"
            iclass="'mb-0'"
          />
        </td>
      </tr>
      <tr class="header">
        <th colspan="2" class="smallpadding text-right pr-10"><u>Total Cost:</u></th>
        <td><text-block v-model="data['question14']" :editable="isEditable" iclass="'mb-0'" /></td>
      </tr>
    </table>

    <h4 class="mb-5">Task 3: The No-Cost Challenge!</h4>
    <p class="mb-5"><strong>i) “The Before”:</strong></p>
    <p class="mb-5">
      Sometimes, especially when starting as an interior designer, you will face the challenge of
      trying to achieve a lot with a little. Your client may be on a tight budget and you won’t have
      the budget to buy lots of new things to improve a room.
    </p>
    <p class="mb-5">
      In this challenge, you’re going to work with a budget of $0 and STILL make improvements to
      your room.
    </p>
    <p class="mb-5">
      Look over your bedroom closely. Casting a critical eye over the room in its entirety,
      mercilessly assess it in terms of the 6 key elements of home decor we’ve learned about. You
      should make some notes while doing so.
    </p>
    <p class="mb-5">Take a BEFORE photograph and upload it below.</p>
    <fileupload-button-immediate
      index="1"
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      filetype="photo"
    />

    <p class="mb-5"><strong>ii) “The After”:</strong></p>
    <p class="mb-5">
      Now, it’s time to think of what improvements you can make. Making sure it’s tidy can be a good
      first place to start!
    </p>
    <p class="mb-5">
      This exercise may be as much about what you take out as what you’ll put in. Remember to refer
      back to the 6 basic elements frequently.
    </p>
    <p class="mb-5">
      Check out this article to get some great ideas from interior designers on how to improve your
      bedroom for free (click the image below):
    </p>

    <a
      href="https://www.insider.com/how-to-make-bedroom-look-better-for-free-interior-designers#restyle-the-tops-of-your-dressers-shelves-and-nightstands-6"
      target="_blank"
    >
      <centered-image-wrapper
        :image="require('@/assets/images/experiences/creativeindustries/no_cost_challenge.jpg')"
        alt="Read how to make a bedroom look better for free (opens in a new tab)"
      />
    </a>
    <p class="mb-5">
      Once you’ve made all your improvements, take your AFTER photo and upload it below.
    </p>
    <fileupload-button-immediate
      index="2"
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      filetype="photo"
    />

    <p class="mb-5">
      Compare the two photos side-by-side. Which improvements are you most proud of?
    </p>

    <h4 class="mb-5">Task 4: Moodboard: Your Bedroom Beyond Budget Constraints</h4>
    <h4 class="mb-5">What is a Moodboard?</h4>

    <div class="vx-row flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
        <p class="mb-5">
          A mood board (also called a design board or inspiration board) is a starting point in the
          design process. Essentially, it is a collage of colours, textures, and objects that helps
          an interior designer brainstorm the way a room will look.
        </p>
        <p class="mb-5">
          Mood boards can use magazine cutouts, or an app such as Canva, to create designs for your
          dream room. Designers often refer to these as aesthetics.
        </p>
        <p class="mb-5">
          In this activity, you’re going to use Canva to make a digital mood board for your bedroom.
          You’re going to gather together designs, objects, and ideas you would use in your bedroom
          if money was no object. You can upload your mood board for your dream bedroom here when
          you’ve finished.
        </p>
        <p class="mb-5">
          The first step is to set up a free Canva account. To do this, go to
          <a href="https://www.canva.com" target="_blank">https://www.canva.com</a> and use your
          email to set up a free Canva account with Canva and then log in.
        </p>
        <p class="mb-5">
          Canva is a fantastic graphic design platform, used to create social media graphics,
          presentations, posters, documents, and other visual content. The app already includes lots
          of free templates and functions for users to use. You can pay more for increased
          functionality, for now, the free version is more than enough for your needs.
        </p>
        <p class="mb-5">The videos on the right will walk you through how to do this.</p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-2/3 w-full my-5">
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: How to create a mood board in Canva"
          @click="showVideoPopup(videos[3].video)"
        >
          <img :src="videos[3].imgSrc" class="img-responsive mx-auto mb-5 cursor-pointer" alt="" />
        </button>
        <button
          type="button"
          class="no-style img-click-button"
          aria-label="Play video: A really simple way to complete this task in Canva"
          @click="showVideoPopup(videos[4].video)"
        >
          <img :src="videos[4].imgSrc" class="img-responsive mx-auto mb-5 cursor-pointer" alt="" />
        </button>
      </div>
    </div>

    <p class="mb-5">
      When you’ve completed your mood board, you can upload it to this site for safekeeping.
    </p>
    <p class="mb-5">
      Once your mood board has been completed for your dream bedroom, what about your dream kitchen?
      Living room? Bathroom?
    </p>

    <fileupload-button-immediate
      index="3"
      v-model="uploaded_files['uploaded_file_3']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Mood Board'"
      filetype="photo_doc"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/creativeindustries/video1.jpg'),
          video: 'https://www.youtube.com/embed/OvKy9uPmI5Y',
        },
        2: {
          image: require('@/assets/images/experiences/creativeindustries/how_to_complete_activity_1_trigger.jpg'),
          video: 'https://www.youtube.com/embed/EJRwAxdQyLM',
        },
        3: {
          imgSrc: require('@/assets/images/experiences/creativeindustries/mood_board_360_trigger_1.jpg'),
          video: 'https://www.youtube.com/embed/EJRwAxdQyLM?autoplay=1',
        },
        4: {
          imgSrc: require('@/assets/images/experiences/creativeindustries/mood_board_simple_trigger.jpg'),
          video:
            $vjeConfig.VUE_APP_VJE_PUBLIC_OBJECT_STORAGE_URL +
            '/content/creativeindustries/moodboard_simple.mp4',
        },
      },
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
        uploaded_file_3: '',
      },
      elements_of_decor: [
        {
          title: 'Emphasis',
          description:
            'Each room should have an emphasis or focal point. This breaks the monotony of the space and draws a person’s interest when they first enter the room. An interesting piece of furniture or a wall painted a strong colour can serve as a room’s emphasis, for example.',
        },
        {
          title: 'Contrast',
          description:
            'Contrast can bring life and character to a room. It can be achieved by pairing elements of different colours, materials, textures, and sizes. An example of contrast in a bathroom might be pairing a smooth table and sink with a bumpy tiled backsplash.',
        },
        {
          title: 'Rhythm',
          description:
            'Rhythm in design refers to the repetition of colour, shape, contrast, or other design elements to create patterns that help tie the different elements of a room together and move your eye around the room. An example of this might be using the same colour in a rug, a painting, and a wall.',
        },
        {
          title: 'Balance',
          description:
            'Balance is when everything in a room has its place, when there is a visual calm to a space. There are 2 kinds of balance: symmetrical and asymmetrical. Symmetrical balance is when a room is divided into 2 equal halves. Asymmetrical balance is achieved when the visual weight of the different design elements are balanced in the 2 sides of a room, but with exact duplication.',
        },
        {
          title: 'Scale & Proportion',
          description:
            'This is all about the size of the objects in relation to each other within a space. For example, the stools in a kitchen must be of appropriate height for the countertops.',
        },
        {
          title: 'Harmony',
          description:
            'This is all about unifying the different elements in the space. It is the sense that each piece of the design belongs together. Harmony in a room creates unity and cohesion. It is related to the idea of style such as Scandinavian, Minimalist, Industrial, etc.',
        },
      ],
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    for (let i = 1; i <= 4; i++) {
      if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
        this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
      }
    }
  },
};
</script>

<style scoped>
.w-1\/8 {
  width: 12.5% !important;
}
</style>
